import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Minimize2, Layers, Image as ImageIcon, ScanText, Scissors, RotateCw, ImageDown, Stamp, EyeOff, FileText,
  Lock, LockOpen, Eraser, FilePen, Receipt, ArrowUpRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import { ENGINE_TO_SLUG } from "@/data/tools";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const ENGINE_ICONS = {
  compress: Minimize2, merge: Layers, img2pdf: ImageIcon, extract: ScanText, pages: Scissors,
  rotate: RotateCw, pdf2img: ImageDown, pdf2docx: FileText, watermark: Stamp, redact: EyeOff,
  protect: Lock, unlock: LockOpen, scrub: Eraser, edit: FilePen, invoice: Receipt,
};

const FALLBACK = [
  { id: "compress", name: "Compress", tagline: "Shrink PDFs up to 85%" },
  { id: "merge", name: "Merge", tagline: "Many PDFs into one" },
  { id: "img2pdf", name: "Images to PDF", tagline: "JPG / PNG / WEBP in" },
  { id: "extract", name: "Extract text", tagline: "Every word, out" },
  { id: "pages", name: "Split & reorder", tagline: "Keep only what you need" },
  { id: "rotate", name: "Rotate", tagline: "90 / 180 / 270 degrees" },
  { id: "pdf2img", name: "PDF to PNG", tagline: "Every page as an image" },
  { id: "pdf2docx", name: "PDF to Word", tagline: "Editable DOCX from any PDF" },
  { id: "watermark", name: "Watermark", tagline: "Stamp CONFIDENTIAL & more" },
  { id: "redact", name: "Redact", tagline: "Permanent text & box blackouts" },
  { id: "protect", name: "Protect", tagline: "AES-256 password lock" },
  { id: "unlock", name: "Unlock", tagline: "Remove a known password" },
  { id: "scrub", name: "Scrub metadata", tagline: "Strip hidden document data" },
  { id: "edit", name: "Edit PDF", tagline: "Add text, rotate, drop pages" },
  { id: "invoice", name: "Invoice builder", tagline: "Client-ready invoices in seconds" },
];

export const Tools = () => {
  const [engines, setEngines] = useState(FALLBACK);

  useEffect(() => {
    axios.get(`${API}/engines`).then((res) => setEngines(res.data.engines ?? FALLBACK)).catch(() => {});
  }, []);

  return (
    <section id="engines" className="py-24 md:py-32 lg:py-40" data-testid="tools-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: EASE }}
            className="lg:col-span-8"
          >
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-5">01 — Engines</span>
            <h2 className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-2xl" data-testid="tools-title">
              Sixteen engines. One workspace.
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease: EASE, delay: 0.1 }}
            className="lg:col-span-4 self-end"
          >
            <p className="text-base text-fog leading-relaxed mb-5">
              Every engine the site offers, listed in full. No tiers, no locks — one plan runs them all,
              with zero document retention.
            </p>
            <a href="/engines" data-testid="tools-engines-link"
              className="group inline-flex items-center gap-1.5 text-sm font-medium text-azure transition-colors duration-300">
              Deep-dive every engine
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </a>
          </motion.div>
        </div>

        <div className="border-t border-hairline" data-testid="tools-engine-index">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
            {engines.map((e, i) => {
              const Icon = ENGINE_ICONS[e.id] || FileText;
              const slug = ENGINE_TO_SLUG[e.id];
              const row = (
                <>
                  <span className="font-mono text-[10px] tracking-[0.2em] text-mist pt-1.5 w-6 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <Icon className="w-5 h-5 text-ink shrink-0 mt-1 group-hover:text-azure transition-colors duration-300" strokeWidth={1.5} />
                  <div className="min-w-0">
                    <p className="text-base font-medium tracking-tight text-ink">{e.name}</p>
                    <p className="text-sm text-fog leading-relaxed mt-0.5">{e.tagline}</p>
                  </div>
                </>
              );
              const cls = "group flex items-start gap-4 py-6 md:py-7 border-b border-hairline md:px-6 md:odd:pl-0 lg:[&:nth-child(3n+1)]:pl-0";
              return (
                <motion.div
                  key={e.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.06 }}
                  data-testid={`tools-engine-${e.id}`}
                >
                  {slug ? (
                    <Link to={`/${slug}`} className={cls} data-testid={`tools-engine-link-${e.id}`}>{row}</Link>
                  ) : (
                    <div className={cls}>{row}</div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
